import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Typography } from "@strapi/design-system"
import { fractionalRgbToHex, hexOrRgbToRgbString } from "../ColorItem/helpers"

export interface IProductCardPreview {
  imageSrc: string
  sku: string
  brand: string
  productName: string
  color: string
  withoutVariantName?: boolean
}

export const ProductCardPreview: FC<IProductCardPreview> = ({
  imageSrc,
  sku,
  brand,
  productName,
  color,
  withoutVariantName = false,
}) => {
  const { t } = useTranslation()
  const [rbgColor, setRbgColor] = useState<string | null>("")

  useEffect(() => {
    if (color) setRbgColor(hexOrRgbToRgbString(color))
    else setRbgColor(null)
  }, [color])

  return (
    <Box>
      <Typography variant="omega" textColor="neutral600">
        {t("productCardPreview.title")}
      </Typography>
      <Box
        padding={2}
        hasRadius
        shadow="tableShadow"
        background="neutral0"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          width: 230,
          marginTop: "10px",
        }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={productName}
            style={{
              border: "1px solid #DCDCDD",
              width: 208,
              height: 208,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        ) : (
          <Box
            background="neutral200"
            borderRadius="8px"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 208,
              height: 208,
            }}
          >
            <img alt={t("productCardPreview.noImageAlt")} src="/none-image.svg" height="42px" />
          </Box>
        )}

        <Box
          padding={2}
          hasRadius
          background="neutral0"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {withoutVariantName ? null : (
              <Box style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "200px",
                  }}
                >
                  <svg width="30px" height="30px">
                    <circle
                      cx="15"
                      cy="15"
                      r="7.5"
                      stroke="#DDDDDD"
                      strokeWidth={rbgColor ? "0" : "1"}
                      fill={`${rbgColor ? fractionalRgbToHex(rbgColor) : "#fff"}`}
                    />
                  </svg>

                  <Typography
                    variant="omega"
                    textColor="neutral800"
                    style={{
                      maxWidth: "130px",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sku?.length ? sku : t("productCardPreview.defaultVariantName")}
                  </Typography>
                  <img src="/arrow-down.svg" alt="" style={{ marginLeft: "5px" }} />
                </Box>
              </Box>
            )}
            <Typography
              variant="omega"
              textColor="neutral800"
              fontWeight="bold"
              style={{
                maxWidth: "130px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                marginBottom: "3px",
                textAlign: "center",
              }}
            >
              {brand?.length ? brand : t("productCardPreview.defaultBrandName")}
            </Typography>
            <Typography
              variant="omega"
              textColor="neutral800"
              style={{
                maxWidth: "130px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {productName?.length ? productName : t("productCardPreview.defaultProductName")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
